import React from 'react'
import { Info } from 'react-feather'
import { FormatOptionLabelMeta, StylesConfig } from 'react-select'

import { GroupedSelectOption } from '@/components/form/MultiSelectInput'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { THEME_PALETTE_DARK, THEME_PALETTE_LIGHT } from '@/lib/constants'
import { getCssVariable } from '@/lib/helpers/domHelpers'
import { SelectOption } from '@/types/misc'

interface BuildMultiStylesOptions {
  isDisabled?: boolean
  isPdf?: boolean
  isOffCanvas?: boolean
  showOnlyFirstValue?: boolean
  maxMenuHeight?: number
}

export type SelectStyles = StylesConfig<
  SelectOption,
  boolean,
  GroupedSelectOption
>

export type MultiSelectStyles = StylesConfig<
  SelectOption,
  true,
  GroupedSelectOption
>

export type SingleSelectStyles = StylesConfig<
  SelectOption,
  false,
  GroupedSelectOption
>

export function buildMultiSelectStyles(
  options?: BuildMultiStylesOptions,
): MultiSelectStyles {
  return buildSelectStyles<true>(options)
}

export function buildSingleSelectStyles(
  options?: BuildMultiStylesOptions,
): SingleSelectStyles {
  return buildSelectStyles<false>(options)
}

export function buildSelectStyles<IsMulti extends boolean = boolean>(
  options?: BuildMultiStylesOptions,
): StylesConfig<SelectOption, IsMulti, GroupedSelectOption> {
  const { isDisabled, isPdf, isOffCanvas, maxMenuHeight, showOnlyFirstValue } =
    options || {}

  return {
    menu: (provided) => ({
      ...provided,
      marginTop: 0,
      zIndex: 2,
    }),
    control: (provided) => ({
      ...provided,
      color: THEME_PALETTE_DARK.secondary,
      backgroundColor: isDisabled ? getCssVariable('--disabled') : undefined,
      borderColor: getCssVariable('--border'),
      minHeight: isPdf ? 'auto' : provided.minHeight,
      opacity: isDisabled ? '50%' : undefined,
    }),
    menuList: (provided) => {
      return {
        ...provided,
        paddingTop: 0,
        display: isOffCanvas ? 'none' : undefined,
        maxHeight: maxMenuHeight ? `${maxMenuHeight}px` : '400px',
      }
    },
    multiValueLabel: (provided, { getValue, index }) => {
      if (!showOnlyFirstValue) {
        return provided
      }

      const isFirstValue = index === 0
      const numOptionsSelected = getValue().length

      return {
        display: isFirstValue ? 'block' : 'none',
        ':after': {
          fontWeight: 'bold',
          color: THEME_PALETTE_LIGHT.black,
          content:
            numOptionsSelected > 1
              ? `" (${numOptionsSelected} selected)"`
              : '""',
        },
      }
    },
    multiValue: (provided) => (showOnlyFirstValue ? {} : provided),
    multiValueRemove: (provided) =>
      showOnlyFirstValue ? { display: 'none' } : provided,
    group: (provided) => ({
      ...provided,
      margin: 0,
      paddingTop: 0,
      paddingBottom: '6px',
    }),
    groupHeading: (provided) => ({
      ...provided,
      borderTop: `1px solid ${THEME_PALETTE_DARK['gray-800']}`,
      color: THEME_PALETTE_DARK.secondary,
      fontSize: '15px',
      fontWeight: 'bold',
      marginTop: 0,
      paddingTop: '12px',
      textTransform: 'none',
    }),
    clearIndicator: (provided, props) => {
      return props.selectProps.menuIsOpen ? { display: 'none' } : provided
    },
    option: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      color: 'inherit',
      ':active': {
        backgroundColor: 'transparent',
      },
    }),
  }
}

export function formatOptionLabel(
  option: SelectOption,
  meta: FormatOptionLabelMeta<SelectOption>,
) {
  const { label, tooltipHint, value } = option
  const { context, selectValue } = meta

  if (context !== 'menu') {
    return label
  }

  const isSelected = selectValue
    .map((selectedValue) => selectedValue.value)
    .includes(value)

  return (
    <div className="flex flex-1 items-start">
      <Checkbox
        className="block"
        checked={isSelected}
        style={{ minWidth: '16px', minHeight: '16px', marginRight: '9px' }}
      />

      <div>
        {label}

        {tooltipHint && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info
                className="text-primary"
                size={16}
                style={{ marginTop: '-2px', marginLeft: '6px' }}
              />
            </TooltipTrigger>

            <TooltipContent>{tooltipHint?.text}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
